<template>
  <v-dialog v-model="dialog" max-width="560" @click:outside="close">
    <v-card class="gcad">
      <div class="gcad__head">
        <div class="gcad__eyebrow">{{ $t("gcAttendance.eyebrow") }}</div>
        <div class="gcad__title">
          {{ session.title || $t("generalEvents.conversationClass") }}
        </div>
        <div class="gcad__sub">
          {{ session.typeLabel }} · {{ session.startsAtLabel }}
        </div>
      </div>

      <v-card-text class="gcad__body">
        <page-skeleton v-if="loading" variant="list" bare :rows="4" />

        <p v-else-if="session.isFuture" class="gcad__note">
          {{ $t("gcAttendance.opensLater") }}
        </p>

        <template v-else>
          <!-- Nem todo mundo que aparece na aula confirmou antes. A busca é o
               caminho para incluir essas pessoas na chamada. -->
          <div class="gcad__search">
            <v-text-field
              v-model="searchTerm"
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-account-search-outline"
              :label="$t('gcAttendance.searchLabel')"
              :loading="searching"
            />
            <p class="gcad__search-hint">
              {{ $t("gcAttendance.searchHint") }}
            </p>

            <div v-if="searchResults.length" class="gcad__results">
              <button
                v-for="result in searchResults"
                :key="result.customerId"
                type="button"
                class="gcad__result"
                :disabled="result.inRoster"
                @click="addStudent(result)"
              >
                <span class="gcad__who">
                  <span class="gcad__name">{{ result.name }}</span>
                  <span class="gcad__level">
                    {{ levelLabel(result.level) }}
                    <template v-if="result.sameLevel === false">
                      {{ $t("gcAttendance.outOfLevel") }}
                    </template>
                  </span>
                </span>
                <span class="gcad__result-cta">
                  {{
                    result.inRoster
                      ? $t("gcAttendance.inRoster")
                      : $t("gcAttendance.add")
                  }}
                </span>
              </button>
            </div>

            <p
              v-else-if="searchDone && !searching"
              class="gcad__note gcad__note--tight"
            >
              {{ $t("gcAttendance.notFound") }}
            </p>
          </div>

          <p v-if="!students.length" class="gcad__note">
            {{ $t("gcAttendance.nobody") }}
          </p>

          <div v-else class="gcad__roster">
            <div
              v-for="student in students"
              :key="student.customerId"
              class="gcad__row"
            >
              <div class="gcad__who">
                <span class="gcad__name">
                  {{ student.name }}
                  <span v-if="student.addedByTeacher" class="gcad__tag">
                    {{ $t("gcAttendance.addedByYou") }}
                  </span>
                </span>
                <span class="gcad__level">{{ levelLabel(student.level) }}</span>
              </div>

              <div class="gcad__toggle">
                <button
                  type="button"
                  class="gcad__btn"
                  :class="{
                    'gcad__btn--present': draft[student.customerId] === 1,
                  }"
                  @click="setStatus(student.customerId, 1)"
                >
                  <v-icon size="15">mdi-check</v-icon>
                  {{ $t("common.present") }}
                </button>
                <button
                  type="button"
                  class="gcad__btn"
                  :class="{
                    'gcad__btn--absent': draft[student.customerId] === 2,
                  }"
                  @click="setStatus(student.customerId, 2)"
                >
                  <v-icon size="15">mdi-close</v-icon>
                  {{ $t("common.absent") }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </v-card-text>

      <div class="gcad__actions">
        <button type="button" class="gcad__cta gcad__cta--ghost" @click="close">
          {{ $t("common.close") }}
        </button>
        <button
          v-if="canSave"
          type="button"
          class="gcad__cta"
          :disabled="saving || !isDirty"
          @click="save"
        >
          {{ saving ? $t("common.saving") : $t("gcAttendance.save") }}
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import { ENGLISH_LEVELS_WITH_CONVERSATION } from "@/shared/classPresentation";

export default {
  name: "GeneralClassAttendanceDialog",

  data() {
    return {
      dialog: false,
      loading: false,
      saving: false,
      sessionId: null,
      session: {},
      students: [],
      draft: {},
      snapshot: {},
      searchTerm: null,
      searchResults: [],
      searching: false,
      searchDone: false,
      searchTimer: null,
      searchToken: 0,
    };
  },

  computed: {
    canSave() {
      return (
        !this.loading && !this.session.isFuture && this.students.length > 0
      );
    },
    isDirty() {
      return this.students.some(
        (student) =>
          this.draft[student.customerId] !== this.snapshot[student.customerId]
      );
    },
  },

  watch: {
    // Debounce de 400ms: o campo dispara a cada tecla, e a rota da API tem rate
    // limit. Sem isto, digitar um nome inteiro queima o limite sozinho.
    searchTerm(value) {
      clearTimeout(this.searchTimer);
      const term = (value || "").trim();

      if (term.length < 2) {
        this.searchResults = [];
        this.searchDone = false;
        this.searching = false;
        return;
      }

      this.searching = true;
      this.searchTimer = setTimeout(() => this.runSearch(term), 400);
    },
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    open(sessionId) {
      this.sessionId = sessionId;
      this.dialog = true;
      this.load();
    },
    close() {
      clearTimeout(this.searchTimer);
      this.dialog = false;
      this.students = [];
      this.session = {};
      this.draft = {};
      this.snapshot = {};
      this.searchTerm = null;
      this.searchResults = [];
      this.searching = false;
      this.searchDone = false;
    },
    runSearch(term) {
      // Respostas fora de ordem não podem sobrescrever uma busca mais recente.
      const token = ++this.searchToken;

      partnerApi
        .searchGeneralClassStudents(this.sessionId, term)
        .then((res) => {
          if (token !== this.searchToken) return;

          const alreadyInRoster = new Set(
            this.students.map((student) => student.customerId)
          );

          this.searchResults = (res.data?.students || []).map((student) => ({
            ...student,
            inRoster:
              student.inRoster || alreadyInRoster.has(student.customerId),
          }));
          this.searchDone = true;
        })
        .catch((err) => {
          if (token !== this.searchToken) return;
          this.searchResults = [];
          this.searchDone = true;
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("gcAttendance.searchFailed"),
            type: 1,
          });
        })
        .finally(() => {
          if (token === this.searchToken) this.searching = false;
        });
    },
    // Só entra na lista local; a participação é criada no servidor quando o
    // professor salva a chamada com a presença marcada.
    addStudent(result) {
      if (result.inRoster) return;

      this.students = [
        ...this.students,
        {
          customerId: result.customerId,
          name: result.name,
          level: result.level,
          attendance: null,
          addedByTeacher: true,
        },
      ];
      this.$set(this.snapshot, result.customerId, null);
      this.$set(this.draft, result.customerId, null);

      this.searchResults = this.searchResults.map((item) =>
        item.customerId === result.customerId
          ? { ...item, inRoster: true }
          : item
      );
    },
    load() {
      this.loading = true;
      partnerApi
        .getGeneralClassRoster(this.sessionId)
        .then((res) => {
          this.session = res.data || {};
          this.students = res.data?.students || [];
          this.snapshot = this.students.reduce((acc, student) => {
            // Número sempre: com "1" nenhum botão acendia e a chamada salva
            // parecia perdida ao reabrir (card 86ajhjzyc).
            acc[student.customerId] =
              student.attendance == null ? null : +student.attendance;
            return acc;
          }, {});
          this.draft = { ...this.snapshot };
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("gcAttendance.openFailed"),
            type: 1,
          });
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setStatus(customerId, status) {
      this.$set(
        this.draft,
        customerId,
        this.draft[customerId] === status ? null : status
      );
    },
    levelLabel(level) {
      if (level === null || level === undefined)
        return this.$t("common.awaitingPlacement");
      return (
        ENGLISH_LEVELS_WITH_CONVERSATION.find((item) => item.value === +level)
          ?.text || "-"
      );
    },
    save() {
      // Envia só quem foi marcado: deixar em branco é "ainda não registrei",
      // e não uma falta.
      const attendances = this.students
        .filter((student) => this.draft[student.customerId])
        .map((student) => ({
          customerId: student.customerId,
          status: this.draft[student.customerId],
        }));

      if (!attendances.length) {
        this.$root.$emit("display-alert", {
          text: this.$t("gcAttendance.markOne"),
          type: 1,
        });
        return;
      }

      this.saving = true;
      partnerApi
        .saveGeneralClassAttendance(this.sessionId, attendances)
        .then(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("gcAttendance.saved"),
            type: 0,
          });
          this.close();
        })
        .catch((err) => {
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message ||
              this.$t("gcAttendance.saveFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.gcad {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --teal: var(--dk-teal, #00a379);
  --red: var(--dk-red, #d52323);
  border-radius: 20px !important;
  color: var(--ink);
}

.gcad__head {
  padding: 22px 24px 6px;
}

.gcad__search {
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--line);
}
.gcad__search-hint {
  margin: 8px 0 0;
  font-size: 0.76rem;
  color: var(--muted);
}
.gcad__results {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 190px;
  overflow-y: auto;
}
.gcad__result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: 11px;
  background: var(--dk-surface, #fff);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.gcad__result:hover:not(:disabled) {
  border-color: var(--teal);
  background: rgba(0, 163, 121, 0.05);
}
.gcad__result:disabled {
  cursor: default;
  opacity: 0.55;
}
.gcad__result-cta {
  flex-shrink: 0;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--teal);
}
.gcad__result:disabled .gcad__result-cta {
  color: var(--muted);
}
.gcad__tag {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(0, 163, 121, 0.12);
  color: var(--teal);
  font-size: 0.66rem;
  font-weight: 700;
  white-space: nowrap;
}
.gcad__note--tight {
  margin-top: 10px;
}
.gcad__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--teal);
}
.gcad__title {
  margin-top: 4px;
  font-size: 1.3rem;
  font-weight: 800;
  line-height: 1.2;
}
.gcad__sub {
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--muted);
}

.gcad__body {
  padding: 16px 24px 4px !important;
}
.gcad__state {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.88rem;
  color: var(--muted);
}
.gcad__note {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.55;
  color: var(--muted);
}

.gcad__roster {
  display: grid;
  gap: 8px;
}
.gcad__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
}
.gcad__who {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.gcad__name {
  font-size: 0.92rem;
  font-weight: 700;
}
.gcad__level {
  font-size: 0.76rem;
  color: var(--muted);
}
.gcad__toggle {
  display: flex;
  gap: 6px;
  flex: 0 0 auto;
}
.gcad__btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.gcad__btn:focus-visible {
  outline: 2px solid var(--teal);
  outline-offset: 2px;
}
.gcad__btn--present {
  border-color: var(--teal);
  background: rgba(0, 163, 121, 0.1);
  color: var(--dk-teal-deep, #007f5e);
}
.gcad__btn--absent {
  border-color: var(--red);
  background: rgba(213, 35, 35, 0.09);
  color: var(--dk-red, #a61b1b);
}

.gcad__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
}
.gcad__cta {
  padding: 9px 18px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(
    160deg,
    var(--dk-teal, #00a379),
    var(--dk-teal-deep, #007f5e)
  );
  color: #fff;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
}
.gcad__cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.gcad__cta--ghost {
  background: transparent;
  color: var(--ink);
}
.gcad__cta:focus-visible {
  outline: 2px solid var(--teal);
  outline-offset: 2px;
}

@media (max-width: 480px) {
  .gcad__row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
